import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { userAxios, setupAxiosInterceptors } from '../userAxios';
import colorblindMappings from '../data/colorblindMappings';
const API_URL = process.env.REACT_APP_API_URL;

export const UserContext = React.createContext('');

const UserProvider = (props) => {
  const storedDarkModePref = localStorage.getItem('darkMode');
  const storedModalPref = localStorage.getItem('isExpanded');
  const storedActivePref = localStorage.getItem('hideInActive');
  const storedDeniedPref = localStorage.getItem('hideDenied');
  const storedVendorColumnVisibility = localStorage.getItem('vendorColumnVisibility');
  const storedAccessibilityButtonPosition = localStorage.getItem('accessibilityButtonPosition');
  const storedAccessibilityHidden = localStorage.getItem('accessibilityHidden');
  const storedFontSize = localStorage.getItem('selectedFontSize');

  const initialDarkMode = storedDarkModePref ? storedDarkModePref === 'true' : false;
  const initialModal = storedModalPref ? storedModalPref === 'true' : false;
  const initialActive = storedActivePref ? storedActivePref === 'true' : false;
  const initialDenied = storedDeniedPref ? storedDeniedPref === 'true' : false;
  const initialAccessibilityButtonPosition = storedAccessibilityButtonPosition
    ? parseFloat(storedAccessibilityButtonPosition)
    : window.innerHeight / 2 - 40;
  const initialAccessibilityHidden = storedAccessibilityHidden === 'true';
  const initialFontSize = storedFontSize || '16px';

  const [colorblindMode, setColorblindMode] = useState(localStorage.getItem('colorblindMode') || 'none');
  const [localMode, setLocalMode] = useState(colorblindMode);
  const [localFont, setLocalFont] = useState(localStorage.getItem('selectedFont') || 'none');
  const [localFontSize, setLocalFontSize] = useState(initialFontSize);
  const [darkMode, setDarkMode] = useState(initialDarkMode);
  const navigate = useNavigate();
  const location = useLocation();
  const [tokenValid, setTokenValid] = useState('');
  const [user, setUser] = useState({});
  const [ticket, setTicket] = useState({});
  const [isExpanded, setIsExpanded] = useState(initialModal);
  const [hideInActive, setHideInActive] = useState(initialActive);
  const [hideDenied, setHideDenied] = useState(initialDenied);
  const [accessibilityButtonPosition, setAccessibilityButtonPosition] = useState(initialAccessibilityButtonPosition);
  const [accessibilityHidden, setAccessibilityHidden] = useState(initialAccessibilityHidden);
  const [intendedRoute, setIntendedRoute] = useState(null);
  const defaultWebsiteColumnVisibility = [
    { columnName: "vendor", visibility: true, width: 250 },
    { columnName: "domainName", visibility: true, width: 250 },
    { columnName: "licensee", visibility: true, width: 170 },
    { columnName: "Status", visibility: true, width: 180 },
    { columnName: "office", visibility: true, width: 200 },
    { columnName: "mlsId", visibility: true, width: 160 },
    { columnName: "memberNumber", visibility: true, width: 160 },
    { columnName: "mlsStatus", visibility: true, width: 160 },
    { columnName: "startDate", visibility: true, width: 160 },
    { columnName: "termDate", visibility: true, width: 160 },
  ];

  const [websiteColumnVisibility, setWebsiteColumnVisibility] = useState(() => {
    const stored = localStorage.getItem('websiteColumnVisibility');
    let parsed;
    try {
      parsed = stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse websiteColumnVisibility from localStorage:", e);
      parsed = null;
    }
    return Array.isArray(parsed) ? parsed : defaultWebsiteColumnVisibility;
  });

  const [vendorWebsiteColumnVisibility, setVendorWebsiteColumnVisibility] = useState(() => {
    const stored = localStorage.getItem('vendorWebsiteColumnVisibility');
    let parsed;
    try {
      parsed = stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse vendorWebsiteColumnVisibility from localStorage:", e);
      parsed = null;
    }
    return Array.isArray(parsed) ? parsed : [
      { columnName: 'vendor', visibility: false, width: 190 },
      { columnName: 'domainName', visibility: true, width: 173 },
      { columnName: 'licensee', visibility: true, width: 159 },
      { columnName: 'mlsStatus', visibility: true, width: 156 },
      { columnName: 'status', visibility: true, width: 166 },
      { columnName: 'office', visibility: true, width: 203 },
      { columnName: 'mlsId', visibility: false, width: 153 },
      { columnName: 'memberNumber', visibility: true, width: 160 },
      { columnName: 'startDate', visibility: true, width: 149 },
      { columnName: 'termDate', visibility: true, width: 160 },
    ];
  });

  const [vendorColumnVisibility, setVendorColumnVisibility] = useState(() => {
    const stored = storedVendorColumnVisibility ? JSON.parse(storedVendorColumnVisibility) : null;
    return Array.isArray(stored) ? stored : [
      { columnName: "Vendor", visibility: true, width: 230 },
      { columnName: "Vendor Code", visibility: true, width: 150 },
      { columnName: "Status", visibility: true, width: 180 },
      { columnName: "feedTypeString", visibility: true, width: 200 },
      { columnName: "vendorTypeString", visibility: true, width: 200 },
      { columnName: "activeSites", visibility: true, width: 100 },
    ];
  });

  const [hideInActiveUsers, setHideInActiveUsers] = useState(() => {
    const storedPreference = localStorage.getItem('hideInActiveUsers');
    return storedPreference ? JSON.parse(storedPreference) : false;
  });

  const initState = () => {
    const storedRole = localStorage.getItem('role') || '';
    const storedToken = localStorage.getItem('token') || '';
    if (storedRole && storedToken) {
      return { role: storedRole, token: storedToken, errMsg: '', loggedIn: true };
    }
    return { role: '', token: '', errMsg: 'User not found in local storage', loggedIn: false };
  };

  const [userState, setUserState] = useState(initState);
  const [bootstrap, setBootstrap] = useState(false);

  const sendBootstrap = async () => {
    try {
      const response = await userAxios.post(`${API_URL}/bootstrap`, {}, {});
      return response.data;
    } catch (error) {
      error.message = 'There was an error, contact administrator.';
      throw error;
    }
  };

  const getBootstrap = async () => {
    try {
      const response = await userAxios.get(`${API_URL}/bootstrap`);
      setBootstrap(response.data.needsBootstrap);
      return response.data.needsBootstrap;
    } catch (error) {
      console.error('Error Getting Bootstrap:', error);
      return null;
    }
  };

  const handleAuthErr = (errMsg) => {
    setUserState((prevState) => ({ ...prevState, errMsg }));
  };

  const resetAuthErr = () => {
    setUserState((prevState) => ({ ...prevState, errMsg: '' }));
  };

  const handleInvalidToken = () => {
    logout();
    navigate('/login');
  };

  const login = async (credentials) => {
    try {
      const response = await userAxios.post(`${API_URL}/login`, {}, { auth: credentials });
      const { role, token } = response.data;
      if (role === 'Vendor') {
        handleAuthErr('Unauthorized');
        handleInvalidToken();
        throw new Error('Unauthorized');
      }
      localStorage.setItem('token', token);
      localStorage.setItem('role', role);
      setTokenValid('valid');
      setUserState((prevUserState) => ({ ...prevUserState, role, token, loggedIn: true }));
      navigate(intendedRoute || '/vendors');
      setIntendedRoute(null);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        handleAuthErr(err.response.data.errMsg);
      }
      throw err;
    }
  };

  const register = async ({ username, password, registrationToken }) => {
    try {
      const response = await userAxios.put(
        `${API_URL}/profile/register`,
        { username, password },
        { headers: { Authorization: `Bearer ${registrationToken}`, 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      if (error.response?.status === 409) {
        error.message = 'Username is not available. Please try another one.';
      } else if (error.response?.status === 500) {
        error.message = 'There was an error, contact administrator.';
      }
      throw error;
    }
  };

  const resetPasswordRequest = async (username) => {
    try {
      await userAxios.post(`${API_URL}/forgotpassword`, { username }, {
        headers: { Authorization: `Bearer ${userState.token}` },
      });
    } catch (error) {
      throw error;
    }
  };

  const redirectLogin = () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const token = urlSearchParams.get('token');
    const role = urlSearchParams.get('role');
    if (!role || !token) return false;
    const decodedRole = decodeURIComponent(role);
    localStorage.setItem('token', token);
    localStorage.setItem('role', decodedRole);
    setUserState((prevUserState) => ({ ...prevUserState, token, role: decodedRole, loggedIn: true }));
    return true;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('colorblindMode');
    localStorage.removeItem('selectedFont');
    localStorage.removeItem('selectedFontSize');
    localStorage.removeItem('darkMode');
    localStorage.removeItem('isExpanded');
    localStorage.removeItem('hideInActive');
    localStorage.removeItem('hideDenied');
    localStorage.removeItem('hideInActiveUsers');
    localStorage.removeItem('websiteColumnVisibility');
    localStorage.removeItem('vendorWebsiteColumnVisibility');
    localStorage.removeItem('vendorColumnVisibility');
    localStorage.removeItem('accessibilityButtonPosition');
    localStorage.removeItem('accessibilityHidden');
    setUserState({ role: {}, token: '', errMsg: '', loggedIn: false });
    document.body.classList.remove('dark', 'cb-protanopia', 'cb-protanomaly', 'cb-deuteranopia', 'cb-tritanopia', 'cb-tritanomaly', 'cb-achromatopsia');
  };

  const getUser = async () => {
    try {
      const response = await userAxios.get(`${API_URL}/profile`, {
        headers: { Authorization: `Bearer ${userState.token}` },
      });
      const userData = response.data;
      setUserState((prevUserState) => ({
        ...prevUserState,
        user: response.data,
        username: userData.username,
        email: userData.email,
      }));
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const submitTicket = async (newTicket, ticketType) => {
    if (!newTicket || !ticketType || !userState.email || !userState.username) {
      throw new Error('Missing required fields');
    }
    try {
      newTicket.type = ticketType;
      newTicket.email = userState.email;
      newTicket.username = userState.username;
      const response = await userAxios.post(`${API_URL}/ticket`, newTicket, {
        headers: { Authorization: `Bearer ${userState.token}` },
      });
      return response.data;
    } catch (error) {
      console.error('Error submitting ticket:', error);
      throw error;
    }
  };

  const VUE_APP_URL = process.env.REACT_APP_VUE_APP_URL;

  const getVueAppUrl = () => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    const redirectUrl = `${VUE_APP_URL.trim()}/login?token=${token}&role=${role}`;
    window.open(redirectUrl, '_blank');
  };

  useEffect(() => {
    localStorage.setItem('colorblindMode', colorblindMode);
    document.body.classList.remove('cb-protanopia', 'cb-protanomaly', 'cb-deuteranopia', 'cb-tritanopia', 'cb-tritanomaly', 'cb-achromatopsia');
    if (colorblindMode !== 'none') {
      document.body.classList.add(`cb-${colorblindMode}`);
    }
    if (darkMode) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
  }, [colorblindMode, darkMode]);

  const getUserPreference = async () => {
    const defaultWebsiteColumnVisibility = [
      { columnName: "vendor", visibility: true, width: 250 },
      { columnName: "domainName", visibility: true, width: 250 },
      { columnName: "licensee", visibility: true, width: 170 },
      { columnName: "Status", visibility: true, width: 180 },
      { columnName: "office", visibility: true, width: 200 },
      { columnName: "mlsId", visibility: true, width: 160 },
      { columnName: "memberNumber", visibility: true, width: 160 },
      { columnName: "mlsStatus", visibility: true, width: 160 },
      { columnName: "startDate", visibility: true, width: 160 },
      { columnName: "termDate", visibility: true, width: 160 },
    ];

    const defaultVendorWebsiteColumnVisibility = [
      { columnName: "vendor", visibility: false, width: 190 },
      { columnName: "domainName", visibility: true, width: 173 },
      { columnName: "licensee", visibility: true, width: 159 },
      { columnName: "mlsStatus", visibility: true, width: 156 },
      { columnName: "status", visibility: true, width: 166 },
      { columnName: "office", visibility: true, width: 203 },
      { columnName: "mlsId", visibility: false, width: 153 },
      { columnName: "memberNumber", visibility: true, width: 160 },
      { columnName: "startDate", visibility: true, width: 149 },
      { columnName: "termDate", visibility: true, width: 160 },
    ];

    const defaultVendorColumnVisibility = [
      { columnName: "Vendor", visibility: true, width: 230 },
      { columnName: "Vendor Code", visibility: true, width: 150 },
      { columnName: "Status", visibility: true, width: 180 },
      { columnName: "feedTypeString", visibility: true, width: 200 },
      { columnName: "vendorTypeString", visibility: true, width: 200 },
      { columnName: "activeSites", visibility: true, width: 100 },
    ];

    try {
      const response = await userAxios.get(`${API_URL}/profile/preferences`, {
        headers: { Authorization: `Bearer ${userState.token}` },
      });
      const preferences = response.data;

      const newDarkMode = preferences.darkMode === "true";
      const newIsExpanded = preferences.isExpanded === "true";
      const newHideInActive = preferences.hideInActive === "true";
      const newHideDenied = preferences.hideDenied === "true";
      const newColorblindMode = preferences.colorblindMode || "none";
      const newLocalFont = preferences.selectedFont || "none";
      const newLocalFontSize = preferences.selectedFontSize || "16px";

      let newHideInActiveUsers;
      try {
        newHideInActiveUsers = preferences.hideInActiveUsers
          ? JSON.parse(preferences.hideInActiveUsers)
          : false;
      } catch (e) {
        console.error("Failed to parse hideInActiveUsers:", e);
        newHideInActiveUsers = false;
      }

      let newWebsiteColumnVisibility;
      try {
        newWebsiteColumnVisibility = preferences.websiteColumnVisibility
          ? JSON.parse(preferences.websiteColumnVisibility)
          : defaultWebsiteColumnVisibility;
      } catch (e) {
        console.error("Failed to parse websiteColumnVisibility:", e);
        newWebsiteColumnVisibility = defaultWebsiteColumnVisibility;
      }
      const validatedWebsiteColumnVisibility = Array.isArray(newWebsiteColumnVisibility)
        ? newWebsiteColumnVisibility
        : defaultWebsiteColumnVisibility;

      let newVendorWebsiteColumnVisibility;
      try {
        newVendorWebsiteColumnVisibility = preferences.vendorWebsiteColumnVisibility
          ? JSON.parse(preferences.vendorWebsiteColumnVisibility)
          : defaultVendorWebsiteColumnVisibility;
      } catch (e) {
        console.error("Failed to parse vendorWebsiteColumnVisibility:", e);
        newVendorWebsiteColumnVisibility = defaultVendorWebsiteColumnVisibility;
      }
      const validatedVendorWebsiteColumnVisibility = Array.isArray(newVendorWebsiteColumnVisibility)
        ? newVendorWebsiteColumnVisibility
        : defaultVendorWebsiteColumnVisibility;

      let newVendorColumnVisibility;
      try {
        newVendorColumnVisibility = preferences.vendorColumnVisibility
          ? JSON.parse(preferences.vendorColumnVisibility)
          : defaultVendorColumnVisibility;
      } catch (e) {
        console.error("Failed to parse vendorColumnVisibility:", e);
        newVendorColumnVisibility = defaultVendorColumnVisibility;
      }
      const validatedVendorColumnVisibility = Array.isArray(newVendorColumnVisibility)
        ? newVendorColumnVisibility
        : defaultVendorColumnVisibility;

      const newAccessibilityButtonPosition = preferences.accessibilityButtonPosition
        ? parseFloat(preferences.accessibilityButtonPosition)
        : window.innerHeight / 2 - 40;
      const newAccessibilityHidden = preferences.accessibilityHidden === "true";

      setDarkMode(newDarkMode);
      setIsExpanded(newIsExpanded);
      setHideInActive(newHideInActive);
      setHideDenied(newHideDenied);
      setColorblindMode(newColorblindMode);
      setLocalFont(newLocalFont);
      setLocalFontSize(newLocalFontSize);
      setHideInActiveUsers(newHideInActiveUsers);
      setWebsiteColumnVisibility(validatedWebsiteColumnVisibility);
      setVendorWebsiteColumnVisibility(validatedVendorWebsiteColumnVisibility);
      setVendorColumnVisibility(validatedVendorColumnVisibility);
      setAccessibilityButtonPosition(newAccessibilityButtonPosition);
      setAccessibilityHidden(newAccessibilityHidden);

      localStorage.setItem("darkMode", newDarkMode.toString());
      localStorage.setItem("isExpanded", newIsExpanded.toString());
      localStorage.setItem("hideInActive", newHideInActive.toString());
      localStorage.setItem("hideDenied", newHideDenied.toString());
      localStorage.setItem("colorblindMode", newColorblindMode);
      localStorage.setItem("selectedFont", newLocalFont);
      localStorage.setItem("selectedFontSize", newLocalFontSize);
      localStorage.setItem("hideInActiveUsers", JSON.stringify(newHideInActiveUsers));
      localStorage.setItem("websiteColumnVisibility", JSON.stringify(validatedWebsiteColumnVisibility));
      localStorage.setItem("vendorWebsiteColumnVisibility", JSON.stringify(validatedVendorWebsiteColumnVisibility));
      localStorage.setItem("vendorColumnVisibility", JSON.stringify(validatedVendorColumnVisibility));
      localStorage.setItem("accessibilityButtonPosition", newAccessibilityButtonPosition.toString());
      localStorage.setItem("accessibilityHidden", newAccessibilityHidden.toString());

      document.body.classList.remove("cb-protanopia", "cb-protanomaly", "cb-deuteranopia", "cb-tritanopia", "cb-tritanomaly", "cb-achromatopsia");
      if (newColorblindMode !== "none") {
        document.body.classList.add(`cb-${newColorblindMode}`);
      }
      if (newDarkMode) {
        document.body.classList.add("dark");
      } else {
        document.body.classList.remove("dark");
      }
    } catch (error) {
      console.error("Error fetching preferences:", error);

      setDarkMode(initialDarkMode);
      setIsExpanded(initialModal);
      setHideInActive(initialActive);
      setHideDenied(initialDenied);
      setColorblindMode("none");
      setLocalFont("none");
      setLocalFontSize(initialFontSize);
      setHideInActiveUsers(false);
      setWebsiteColumnVisibility(defaultWebsiteColumnVisibility);
      setVendorWebsiteColumnVisibility(defaultVendorWebsiteColumnVisibility);
      setVendorColumnVisibility(defaultVendorColumnVisibility);
      setAccessibilityButtonPosition(initialAccessibilityButtonPosition);
      setAccessibilityHidden(initialAccessibilityHidden);

      localStorage.setItem("darkMode", initialDarkMode.toString());
      localStorage.setItem("isExpanded", initialModal.toString());
      localStorage.setItem("hideInActive", initialActive.toString());
      localStorage.setItem("hideDenied", initialDenied.toString());
      localStorage.setItem("colorblindMode", "none");
      localStorage.setItem("selectedFont", "none");
      localStorage.setItem("selectedFontSize", initialFontSize);
      localStorage.setItem("hideInActiveUsers", JSON.stringify(false));
      localStorage.setItem("websiteColumnVisibility", JSON.stringify(defaultWebsiteColumnVisibility));
      localStorage.setItem("vendorWebsiteColumnVisibility", JSON.stringify(defaultVendorWebsiteColumnVisibility));
      localStorage.setItem("vendorColumnVisibility", JSON.stringify(defaultVendorColumnVisibility));
      localStorage.setItem("accessibilityButtonPosition", initialAccessibilityButtonPosition.toString());
      localStorage.setItem("accessibilityHidden", initialAccessibilityHidden.toString());

      document.body.classList.remove("cb-protanopia", "cb-protanomaly", "cb-deuteranopia", "cb-tritanopia", "cb-tritanomaly", "cb-achromatopsia");
      if (initialDarkMode) {
        document.body.classList.add("dark");
      } else {
        document.body.classList.remove("dark");
      }
    }
  };

  const setUserPreference = async (newPreferences) => {
    try {
      console.log('Setting preferences - accessibilityHidden:', newPreferences.accessibilityHidden);
      const validatedVendorWebsiteColumnVisibility = Array.isArray(newPreferences.vendorWebsiteColumnVisibility)
        ? newPreferences.vendorWebsiteColumnVisibility
        : [
          { columnName: 'vendor', visibility: false, width: 190 },
          { columnName: 'domainName', visibility: true, width: 173 },
          { columnName: 'licensee', visibility: true, width: 159 },
          { columnName: 'mlsStatus', visibility: true, width: 156 },
          { columnName: 'status', visibility: true, width: 166 },
          { columnName: 'office', visibility: true, width: 203 },
          { columnName: 'mlsId', visibility: false, width: 153 },
          { columnName: 'memberNumber', visibility: true, width: 160 },
          { columnName: 'startDate', visibility: true, width: 149 },
          { columnName: 'termDate', visibility: true, width: 160 },
        ];

      const {
        newDarkMode = darkMode,
        newIsExpanded = isExpanded,
        newHideInActive = hideInActive,
        newHideDenied = hideDenied,
        newColorblindMode = colorblindMode,
        newLocalFont = localFont,
        newLocalFontSize = localFontSize,
        newHideInActiveUsers = hideInActiveUsers,
        newWebsiteColumnVisibility = websiteColumnVisibility,
        newVendorColumnVisibility = vendorColumnVisibility,
        newAccessibilityButtonPosition = accessibilityButtonPosition,
        newAccessibilityHidden = accessibilityHidden,
      } = newPreferences;

      localStorage.setItem('darkMode', newDarkMode.toString());
      localStorage.setItem('isExpanded', newIsExpanded.toString());
      localStorage.setItem('hideInActive', newHideInActive.toString());
      localStorage.setItem('hideDenied', newHideDenied.toString());
      localStorage.setItem('colorblindMode', newColorblindMode);
      localStorage.setItem('selectedFont', newLocalFont);
      localStorage.setItem('selectedFontSize', newLocalFontSize);
      localStorage.setItem('hideInActiveUsers', JSON.stringify(newHideInActiveUsers));
      localStorage.setItem('websiteColumnVisibility', JSON.stringify(newWebsiteColumnVisibility));
      localStorage.setItem('vendorWebsiteColumnVisibility', JSON.stringify(validatedVendorWebsiteColumnVisibility));
      localStorage.setItem('vendorColumnVisibility', JSON.stringify(newVendorColumnVisibility));
      localStorage.setItem('accessibilityButtonPosition', newAccessibilityButtonPosition.toString());
      localStorage.setItem('accessibilityHidden', newAccessibilityHidden.toString());

      setDarkMode(newDarkMode);
      setIsExpanded(newIsExpanded);
      setHideInActive(newHideInActive);
      setHideDenied(newHideDenied);
      setColorblindMode(newColorblindMode);
      setLocalFont(newLocalFont);
      setLocalFontSize(newLocalFontSize);
      setHideInActiveUsers(newHideInActiveUsers);
      setWebsiteColumnVisibility(newWebsiteColumnVisibility);
      setVendorWebsiteColumnVisibility(validatedVendorWebsiteColumnVisibility);
      setVendorColumnVisibility(newVendorColumnVisibility);
      setAccessibilityButtonPosition(newAccessibilityButtonPosition);
      setAccessibilityHidden(newAccessibilityHidden);

      const preferencePayload = {
        darkMode: newDarkMode.toString(),
        isExpanded: newIsExpanded.toString(),
        hideInActive: newHideInActive.toString(),
        hideDenied: newHideDenied.toString(),
        colorblindMode: newColorblindMode,
        selectedFont: newLocalFont,
        selectedFontSize: newLocalFontSize,
        hideInActiveUsers: JSON.stringify(newHideInActiveUsers),
        websiteColumnVisibility: JSON.stringify(newWebsiteColumnVisibility),
        vendorWebsiteColumnVisibility: JSON.stringify(validatedVendorWebsiteColumnVisibility),
        vendorColumnVisibility: JSON.stringify(newVendorColumnVisibility),
        accessibilityButtonPosition: newAccessibilityButtonPosition.toString(),
        accessibilityHidden: newAccessibilityHidden.toString(),
      };

      const response = await userAxios.put(`${API_URL}/profile/preferences`, preferencePayload, {
        headers: { Authorization: `Bearer ${userState.token}` },
      });
      console.log('API response from setUserPreference:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (
      !userState.loggedIn &&
      location.pathname !== '/login' &&
      location.pathname.includes('/notifications')
    ) {
      const route = location.pathname + location.search;
      setIntendedRoute(route);
    }
  }, [location, userState.loggedIn]);

  useEffect(() => {
    setupAxiosInterceptors(handleInvalidToken);
  }, []);

  useEffect(() => {
    const fetchBootstrap = async () => {
      const needsBootstrap = await getBootstrap();
      setBootstrap(needsBootstrap);
    };
    fetchBootstrap();
  }, []);

  return (
    <UserContext.Provider
      value={{
        ...userState,
        role: userState.role,
        username: userState.username,
        bootstrap,
        getBootstrap,
        setBootstrap,
        sendBootstrap,
        ticket,
        user,
        setUser,
        setTicket,
        resetAuthErr,
        handleInvalidToken,
        tokenValid,
        setTokenValid,
        login,
        register,
        redirectLogin,
        logout,
        getVueAppUrl,
        getUser,
        submitTicket,
        darkMode,
        setDarkMode,
        colorblindMode,
        setColorblindMode,
        getUserPreference,
        setUserPreference,
        isExpanded,
        setIsExpanded,
        hideInActive,
        setHideInActive,
        hideDenied,
        setHideDenied,
        hideInActiveUsers,
        setHideInActiveUsers,
        websiteColumnVisibility,
        setWebsiteColumnVisibility,
        vendorWebsiteColumnVisibility,
        setVendorWebsiteColumnVisibility,
        vendorColumnVisibility,
        setVendorColumnVisibility,
        accessibilityButtonPosition,
        setAccessibilityButtonPosition,
        accessibilityHidden,
        setAccessibilityHidden,
        resetPasswordRequest,
        userState,
        localMode,
        setLocalMode,
        localFont,
        setLocalFont,
        localFontSize,
        setLocalFontSize,
        colorblindMappings,
        intendedRoute,
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
};

export default UserProvider;